import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  selectIsAuthenticated,
  selectUserRoleLevel,
  selectHasCompany,
  selectIsCompanyActive,
} from "../../store/slices/authSlice";

/**
 * ProtectedRoute Component
 * Wraps routes that require authentication and/or specific role levels
 *
 * @param {React.ReactNode} children - Components to render if authorized
 * @param {number} requiredLevel - Minimum role level required (optional)
 *   - null/undefined: Only authentication required
 *   - 1-2: Regular users
 *   - 3+: Admin users (Staff/Admin)
 * @param {boolean} requireCompany - Whether user must have a company (optional)
 *
 * Role Levels:
 * - User: 1
 * - User Host: 2
 * - Staff: 3 (Admin access starts here)
 * - Admin: 4
 */
const ProtectedRoute = ({ children, requiredLevel = null, requireCompany = false }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userLevel = useSelector(selectUserRoleLevel);
  const hasCompany = useSelector(selectHasCompany);
  const isCompanyActive = useSelector(selectIsCompanyActive);
  const location = useLocation();

  // Check if user is authenticated
  if (!isAuthenticated) {
    // Redirect to login, save the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user needs to select a company
  if (requireCompany && !hasCompany) {
    // Redirect to company selection
    return <Navigate to="/company-selection" replace />;
  }

  // Check if user's company is inactive (pending admin activation)
  if (requireCompany && hasCompany && !isCompanyActive) {
    return <Navigate to="/company-inactive" replace />;
  }

  // Check if user has required role level
  if (requiredLevel !== null && requiredLevel !== undefined) {
    if (!userLevel || userLevel < requiredLevel) {
      // User doesn't have sufficient permissions
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // User is authenticated and has required permissions
  return children;
};

export default ProtectedRoute;
